/** @format */
import {
    Component,
    DestroyRef,
    EventEmitter,
    Inject,
    inject,
    OnChanges,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CheckboxGroupComponent, FudisCheckboxGroupFormGroup, FudisDialogService } from '@funidata/ngx-fudis';
import { DisclosureAuthorization, DisclosureAuthorizationCategory, OtmId, PrivatePerson } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { combineLatest, finalize, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthService } from 'sis-common';
import { AppErrorHandler } from '../../error-handler/app-error-handler';
import { SisFormBuilder } from '../../form/sis-form-builder.service';
import { DisclosureAuthorizationEntityService } from '../../service/disclosure-authorization-entity.service';

export interface DisclosureAuthorizationsEditModalValues {
    person: PrivatePerson;
    staffEdit: boolean;
    categories: DisclosureAuthorizationCategory[];
    answers: DisclosureAuthorization[];
    reset: () => void;
}

interface DisclosureAuthorizationForm {
    answers: FormGroup<{ [id: OtmId]: FormControl<boolean> }>;
}

export function disclosureAuthorizationsEditModalOpener(): (
    person: PrivatePerson,
    staffEdit: boolean,
    categories: DisclosureAuthorizationCategory[],
    answers: DisclosureAuthorization[],
    reset: () => void,
) => MatDialogRef<DisclosureAuthorizationsEditModalComponent, { data: object }> {
    const dialogService = inject(FudisDialogService);
    return (
        person: PrivatePerson,
        staffEdit: boolean,
        categories: DisclosureAuthorizationCategory[],
        answers: DisclosureAuthorization[],
        reset: () => void,
    ) =>
        dialogService.open(DisclosureAuthorizationsEditModalComponent, {
            data: {
                person,
                staffEdit,
                categories,
                answers,
                reset,
            },
        });
}

@Component({
    selector: 'sis-disclosure-authorizations-edit-modal',
    templateUrl: './disclosure-authorizations-edit-modal.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class DisclosureAuthorizationsEditModalComponent implements OnInit, OnChanges {
    @Output() reset = new EventEmitter<void>();

    @ViewChild('checkboxGroup') public checkboxGroup: CheckboxGroupComponent<FudisCheckboxGroupFormGroup<object>>;

    person: PrivatePerson;
    staffEdit: boolean;
    categories: DisclosureAuthorizationCategory[];
    answers: DisclosureAuthorization[];
    form: FormGroup<DisclosureAuthorizationForm>;
    initialValues: { [id: OtmId]: boolean };

    private requestsInProgress: boolean = false;

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public values: DisclosureAuthorizationsEditModalValues,
        private fb: SisFormBuilder,
        private disclosureAuthorizationService: DisclosureAuthorizationEntityService,
        private appErrorHandler: AppErrorHandler,
        private authService: AuthService,
        private destroyRef: DestroyRef,
        private dialogService: FudisDialogService,
    ) {}

    ngOnInit() {
        if (this.values) {
            this.person = this.values.person;
            this.staffEdit = this.values.staffEdit;
            this.categories = this.values.categories;
            this.answers = this.values.answers;
            this.form = this.buildForm();
            this.reset.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.values.reset);
            this.initialValues = this.form.value.answers;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.categories || changes.answers) {
            this.form = this.buildForm();
            this.initialValues = this.form.value.answers;
        }
    }

    buildForm(): FormGroup<DisclosureAuthorizationForm> {
        return this.fb.group({
            answers: this.fb.group(
                Object.fromEntries(this.categories.map(category => [category.id, _.get(this.getAnswer(category.id), 'authorized', false)])),
            ),
        });
    }

    authorizationCategoriesDefined(): boolean {
        return Object.keys(this.form?.controls?.answers.controls).length > 0;
    }

    getAnswer(disclosureCategoryId: OtmId): DisclosureAuthorization {
        return _.find(this.answers, answer => answer.disclosureCategoryId === disclosureCategoryId);
    }

    getDescription(disclosureCategoryId: OtmId) {
        return _.find(this.categories, category => category.id === disclosureCategoryId).description;
    }

    save(closeDialog = true): void {
        if (this.checkboxGroup?.formGroup) {
            if (this.checkboxGroup.formGroup.invalid) {
                this.checkboxGroup.formGroup.markAllAsTouched();
            } else if (!this.requestsInProgress) {
                this.requestsInProgress = true;
                const observables: Observable<DisclosureAuthorization>[] = [];
                const updatedAnswers = this.getUpdatedAnswersFromForm(this.checkboxGroup.formGroup);

                _.forEach(updatedAnswers, updatedAnswer => {
                    if (_.get(updatedAnswer, 'metadata')) {
                        observables.push(this.disclosureAuthorizationService.updateAnswer(updatedAnswer));
                    } else if (this.staffEdit) {
                        observables.push(this.disclosureAuthorizationService.createForStudent(this.person.id, updatedAnswer));
                    } else {
                        observables.push(this.disclosureAuthorizationService.createNewAnswer(updatedAnswer));
                    }
                });

                observables.length > 0
                    ? combineLatest(observables)
                          .pipe(
                              take(1),
                              finalize(() => (this.requestsInProgress = false)),
                              this.appErrorHandler.defaultErrorHandler(),
                          )
                          .subscribe(() => this.stopEditing(closeDialog))
                    : this.dialogService.close();
            }
        } else {
            this.reset.emit();
        }
    }

    private stopEditing(closeDialog = true): void {
        this.reset.emit();
        if (closeDialog) {
            this.dialogService.close();
        }
    }

    getUpdatedAnswersFromForm(formAnswers: FormGroup<FudisCheckboxGroupFormGroup<object>>): DisclosureAuthorization[] {
        const updatedAnswers: DisclosureAuthorization[] = [];

        const answersArray = Object.entries(this.initialValues).map(([id, authorized]) => ({ id, authorized }));

        Object.keys(formAnswers.controls).forEach(controlId => {
            _.forEach(answersArray, initialAnswer => {
                // If staff edit answers only changed answers are pushed to updatedAnswers array.
                if (
                    initialAnswer.id === controlId &&
                    !(
                        this.staffEdit &&
                        initialAnswer.authorized ===
                            formAnswers.controls[controlId as keyof typeof this.checkboxGroup.formGroup.controls]?.['value']
                    )
                ) {
                    updatedAnswers.push(
                        this.createAnswer(
                            controlId,
                            formAnswers.controls[controlId as keyof typeof this.checkboxGroup.formGroup.controls]?.['value'],
                        ),
                    );
                }
            });
        });
        return updatedAnswers;
    }

    createAnswer(updatedId: string, updatedValue: boolean): DisclosureAuthorization {
        return {
            disclosureCategoryId: updatedId,
            authorized: updatedValue,
            privatePersonId: this.person.id,
            changedByPersonId: this.authService.personId(),
            documentState: _.get(this.getAnswer(updatedId), 'documentState', undefined),
            id: _.get(this.getAnswer(updatedId), 'id', undefined),
            metadata: _.get(this.getAnswer(updatedId), 'metadata', undefined),
        };
    }

    get formAnswers(): FormGroup<{ [urn: string]: FormControl<boolean> }> {
        return !this.form ? null : this.form.controls.answers;
    }
}
